import { defineStore } from 'pinia'
import { UserRank } from '~~/interfaces/user'
import type {
    CommunityUserUserData,
    TeslaLinkStatus,
    UserAddress
} from '~~/interfaces/user'
import type { UserVehicle } from '#cmui/types/vehicle'
import { $fetchApi } from '~~/app/composables/fetchApi'

const useUserStore = defineStore('user', () => {
    const baseUrl = tryUseNuxtApp()
        ? useRuntimeConfig().public.APPLICATION_URL
        : process.env.NUXT_PUBLIC_APPLICATION_URL

    const communityUserUserData = ref<CommunityUserUserData | null>(null)
    const billingAddress = ref<UserAddress | null>(null)
    const rankLoader = ref<boolean>(false)
    const userTeslaLinkStatus = ref<TeslaLinkStatus | null>(null)
    const userAlreadyHasAPass = ref<boolean>(false)
    const chargemapUsersCount = ref<number>(0)
    const isBoostUser = ref<boolean>(false)
    const userVehicles = ref<UserVehicle[]>([])

    const userRank = computed<number>(() => {
        return communityUserUserData.value?.rank || UserRank.MEMBER
    })

    const getUserRankAsParam = computed<number[]>(() => {
        if (communityUserUserData.value?.rank === UserRank.ADMIN) {
            return [2, 3]
        }

        return [2]
    })

    const isAdmin = computed<boolean>(
        () => communityUserUserData.value?.rank === UserRank.ADMIN
    )

    async function fetchCommunityUserUserData(userId: string | undefined) {
        if (!userId || !Number(userId)) {
            return
        }

        const { $throwError } = useNuxtApp()

        try {
            communityUserUserData.value =
                await $fetchApi<CommunityUserUserData>(
                    `${baseUrl}/community-users/users/${userId}`
                )
        } catch (e: any) {
            communityUserUserData.value = null

            $throwError(e, 'Failed to fetch community user data')
        }
    }

    const fetchUserAddresses = async () => {
        const { $throwError } = useNuxtApp()
        const userId = useUserSession().user?.value?.id

        if (!userId) {
            return
        }

        try {
            const response = await $fetchApi<{
                billingAddress: UserAddress | null
            }>(`${baseUrl}/billy/users/${userId}`)

            billingAddress.value = response.billingAddress || null
        } catch (e: any) {
            $throwError(e, `ResourceNotFound: fetchUserAddresses (${userId})`)
        }
    }

    const fetchUserTeslaLinkStatus = async () => {
        const { $throwError } = useNuxtApp()
        const userId = useUserSession().user?.value?.id

        if (userId) {
            try {
                const data: any = await $fetchApi(
                    `${baseUrl}/legacy-api/v2/users/${userId}.json`,
                    { query: { expand: 'default_rfid_pass' } }
                )
                userTeslaLinkStatus.value =
                    data?.response?.content?.defaultRfidPass?.teslaLinkStatus ||
                    null
            } catch (e: any) {
                $throwError(
                    e,
                    `ResourceNotFound: fetchUserTeslaLinkStatus (${
                        userId || 'unknown user'
                    })`
                )
            }
        }
    }

    const checkIfUserHasChargemapPass = async () => {
        const { $throwError } = useNuxtApp()
        const userId = useUserSession().user?.value?.id

        if (userId) {
            try {
                const data: any = await $fetchApi(
                    `${baseUrl}/community/users/${userId}`
                )
                userAlreadyHasAPass.value = !!data?.defaultRfidPassId
            } catch (e: any) {
                $throwError(
                    e,
                    `ResourceNotFound: checkIfUserHasChargemapPass (${
                        userId || 'unknown user'
                    })`
                )
            }
        }
    }

    const fetchChargemapUsersCount = async () => {
        const { $throwError } = useNuxtApp()
        try {
            const [usersCount, _] = await $fetch<[number, string]>(
                `${baseUrl}/users`
            )
            chargemapUsersCount.value = usersCount || 0
        } catch (e: any) {
            chargemapUsersCount.value = 0
            $throwError(e, 'fetchChargemapUsersCount')
        }
    }

    const fetchUserVehicles = async () => {
        const { $throwError, $i18n } = useNuxtApp()
        const userId = useUserSession().user?.value?.id

        if (userId) {
            try {
                const data: any = await $fetchApi(
                    `${baseUrl}/community-users/users/${userId}/vehicles`,
                    {
                        query: {
                            locale: $i18n.locale.value
                        }
                    }
                )

                userVehicles.value = data?.items || []
            } catch (e: any) {
                $throwError(
                    e,
                    `ResourceNotFound: fetchUserVehicles (${
                        userId || 'unknown user'
                    })`
                )
            }
        }
    }

    const setVehicleAsDefault = async (userVehicleId: number) => {
        const { $throwError } = useNuxtApp()
        const userId = useUserSession().user?.value?.id

        if (userId) {
            try {
                await $fetchApi(
                    `${baseUrl}/community-users/users/${userId}/vehicles/${userVehicleId}`,
                    {
                        method: 'PATCH',
                        body: {
                            is_default: true
                        }
                    }
                )

                await fetchUserVehicles()
            } catch (e: any) {
                $throwError(
                    e,
                    `ResourceNotFound: setVehicleAsDefault (${
                        userId || 'unknown user'
                    })`
                )
            }
        }
    }

    const postVehicle = async (versionId: number) => {
        const { $throwError } = useNuxtApp()
        const userId = useUserSession().user?.value?.id

        if (userId) {
            try {
                await $fetchApi(
                    `${baseUrl}/community-users/users/${userId}/vehicles`,
                    {
                        method: 'POST',
                        body: {
                            vehicle_version_id: versionId
                        }
                    }
                )

                await fetchUserVehicles()
            } catch (e: any) {
                $throwError(
                    e,
                    `ResourceNotFound: postVehicle (${
                        userId || 'unknown user'
                    })`
                )
            }
        }
    }

    const deleteVehicle = async (
        userVehicleId: number,
        updateUserVehicles = false
    ) => {
        const { $throwError } = useNuxtApp()
        const userId = useUserSession().user?.value?.id

        if (userId) {
            try {
                await $fetchApi(
                    `${baseUrl}/community-users/users/${userId}/vehicles/${userVehicleId}`,
                    {
                        method: 'DELETE'
                    }
                )

                if (updateUserVehicles) {
                    await fetchUserVehicles()
                }
            } catch (e: any) {
                $throwError(
                    e,
                    `ResourceNotFound: deleteVehicle (${
                        userId || 'unknown user'
                    })`
                )
                throw e
            }
        }
    }

    return {
        userRank,
        communityUserUserData,
        billingAddress,
        rankLoader,
        userTeslaLinkStatus,
        userAlreadyHasAPass,
        chargemapUsersCount,
        isBoostUser,
        userVehicles,
        getUserRankAsParam,
        isAdmin,
        fetchCommunityUserUserData,
        fetchUserAddresses,
        fetchUserTeslaLinkStatus,
        checkIfUserHasChargemapPass,
        fetchChargemapUsersCount,
        fetchUserVehicles,
        setVehicleAsDefault,
        postVehicle,
        deleteVehicle
    }
})

type UserStore = Omit<
    ReturnType<typeof useUserStore>,
    keyof ReturnType<typeof defineStore>
>

export default useUserStore

export type { UserStore }
